/**
 * [PORTABLE] Mutation hooks + the polling helper used by the Selfie pillar.
 * Target path in main project: src/features/onboarding/hooks/use-onboarding-mutations.ts
 */
import { useEffect, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { useToast } from "@/features/toast";

import { onboardingService } from "../api/onboarding.service";
import { useOnboardingUiStore } from "../store/onboarding-ui-store";
import { libraryItemUrl, mediaUrl } from "../utils/media-url";
import {
  ONBOARDING_QUERY_KEYS,
  useGenerationStatus,
} from "./use-onboarding-queries";
import type {
  OnboardingErrorCode,
  Pillar,
  StartGenerationInput,
} from "../types/onboarding.types";
import mixpanel from "mixpanel-browser";


const ERROR_KEY_MAP: Record<OnboardingErrorCode, string> = {
  already_processing: "onboarding.errors.alreadyProcessing",
  selfie_required: "onboarding.errors.selfieRequired",
  level_not_found: "onboarding.errors.levelNotFound",
  generation_not_found: "onboarding.errors.generationNotFound",
  eachlabs_call_failed: "onboarding.errors.eachlabsFailed",
};

function useTranslatedError() {
  const { t } = useTranslation();
  return (err: any) => {
    const code = err?.response?.data?.code || err?.code;
    if (code && typeof code === 'string' && code in ERROR_KEY_MAP) {
      return t(ERROR_KEY_MAP[code as OnboardingErrorCode]);
    }
    const message = err?.response?.data?.message || err?.message;
    return message || t("onboarding.errors.failed");
  };
}

export function useUploadSelfie() {
  const queryClient = useQueryClient();
  const { success, error } = useToast();
  const { t } = useTranslation();
  const translateError = useTranslatedError();
  const setSelectedSelfieLibraryItemId = useOnboardingUiStore(
    (s) => s.setSelectedSelfieLibraryItemId,
  );

  return useMutation({
    mutationFn: (file: File) => onboardingService.uploadSelfie(file),
    onSuccess: (libraryItem) => {
      // 🚀 MİXPANEL: Selfie başarıyla yüklendi
      try {
        mixpanel.track("onboarding_action_success", {
          action_type: "upload",
          level_id: "S0"
        });
      } catch (e) {
        console.warn("Mixpanel error:", e);
      }

      setSelectedSelfieLibraryItemId(libraryItem._id || libraryItem.id || null);
      queryClient.invalidateQueries({ queryKey: ONBOARDING_QUERY_KEYS.progress });
      success(t("onboarding.uploadSuccess"));
    },
    onError: (err) => error(translateError(err)),
  });
}

export function useStartGeneration(pillar: Pillar) {
  const setActiveGenerationId = useOnboardingUiStore((s) => s.setActiveGenerationId);
  const { error } = useToast();
  const translateError = useTranslatedError();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: StartGenerationInput) => onboardingService.startGeneration(input),
    onSuccess: (data: any) => {
      const generatedId = data?.id || data?.generationId || data?._id;
      
      if (generatedId) {
        queryClient.removeQueries({ 
          queryKey: ONBOARDING_QUERY_KEYS.generationStatus(generatedId) 
        });
        setActiveGenerationId(pillar, generatedId);
      } else {
        console.error("Backend generation ID dönmedi!", data);
      }
    },
    onError: (err) => error(translateError(err)),
  });
}

export function useSkipOnboarding() {
  const queryClient = useQueryClient();

  const { success, error } = useToast();
  const { t } = useTranslation();
  const translateError = useTranslatedError();

  return useMutation({
    mutationFn: onboardingService.skipOnboarding,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ONBOARDING_QUERY_KEYS.progress });
      success(t("onboarding.skipped"));
    },
    onError: (err) => error(translateError(err)),
  });
}

export function useGenerateAndPoll(pillar: Pillar = "selfie") {
  const queryClient = useQueryClient();
  const startMutation = useStartGeneration(pillar);
  const generationId = useOnboardingUiStore(
    (s) => s.activeGenerationIdByPillar[pillar],
  );
  const setActiveGenerationId = useOnboardingUiStore((s) => s.setActiveGenerationId);
  const statusQuery = useGenerationStatus(generationId, !!generationId);
  const translateError = useTranslatedError();
  const { error } = useToast();

  const status = statusQuery.data?.status;
  const isDone = status === "completed";
  const isFailed = status === "failed";
  
  const errorMessage = statusQuery.data?.error;
  const levelId = statusQuery.data?.levelId;

  const isPolling = startMutation.isPending || (!!generationId && !isDone && !isFailed);

  // 🚀 İŞTE BURASI: Resim üretimi tamamlandığında çalışır
  useEffect(() => {
    if (isDone || isFailed) {
      // 🚀 MİXPANEL: Üretim işlemi backend'den sonuçlandı!
      try {
        mixpanel.track(isDone ? "onboarding_action_success" : "onboarding_action_failed", {
          action_type: "generate",
          level_id: levelId,
          error_reason: isFailed ? errorMessage : null
        });
      } catch (e) {
        console.warn("Mixpanel error:", e);
      }

      queryClient.invalidateQueries({ queryKey: ONBOARDING_QUERY_KEYS.progress });
      if (isDone) {
        queryClient.invalidateQueries({ queryKey: ONBOARDING_QUERY_KEYS.billingHistory });
      }
    }
  }, [isDone, isFailed, queryClient, levelId, errorMessage]); // <-- dependency array'e levelId ve errorMessage eklendi

  const libItemObj = statusQuery.data?.libraryItemId as any;

  const resultUrl =
    libraryItemUrl(libItemObj) ??
    mediaUrl(statusQuery.data?.imageUrl) ??
    mediaUrl(statusQuery.data?.videoUrl) ??
    mediaUrl(statusQuery.data?.resultUrl);

  return useMemo(
    () => ({
      start: (input: Omit<StartGenerationInput, "pillar">) =>
        startMutation.mutate({ pillar, ...input }),
      reset: () => setActiveGenerationId(pillar, null),
      pillar,
      generationId,
      status,
      isPolling,
      isDone,
      isFailed,
      errorMessage, // 🚀 2. EKSİK OLAN SATIR BURASI! BUNU EKLE!
      resultUrl,
      libraryItem: libItemObj ?? null,
      onCompleted: () => {
        // Modal veya bileşen tarafından çağrılırsa diye buraya da ekliyoruz
        queryClient.invalidateQueries({ queryKey: ONBOARDING_QUERY_KEYS.progress });
        queryClient.invalidateQueries({ queryKey: ONBOARDING_QUERY_KEYS.billingHistory });
      },
      reportError: (err: unknown) => error(translateError(err)),
    }),[
      startMutation,
      pillar,
      generationId,
      status,
      isPolling,
      isDone,
      isFailed,
      resultUrl,
      statusQuery.data,
      libItemObj,
      setActiveGenerationId,
      queryClient,
      error,
      translateError,
    ],
  );
}